import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Typography,
} from "@mui/material";

import { useRolesHook } from '../../../Hooks/Maintenance/Accessibilities/rolesHooks';

import { IconPlus, IconEye, IconEdit, IconTrash } from '@tabler/icons-react';

import { TableContainer } from '../../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';

import QueryBuilderHelper from '../../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../../Utilities/Helpers/DateHelper';
import RouteHelper from '../../../Utilities/Helpers/RouteHelper';
import AccessibilityHelper from '../../../Utilities/Helpers/AccessibilityHelper';

import { CachingEnum } from '../../../Utilities/Enums/CachingEnum';
import { RouteEnum } from '../../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../../Utilities/Enums/ButtonTypeEnum';
import { RolesField } from '../../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../../Utilities/Enums/AdminModulesEnum';
import { OrderByEnum } from '../../../Utilities/Enums/SortEnums';

import { Role } from '../../../Models/Maintenance/Accessibilities/Roles';

import CustomListComponent from '../../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../../CustomViews/Elements/CustomIconButton';
import CustomDeleteConfirmation from '../../../CustomViews/Elements/CustomDeleteConfirmation/CustomDeleteConfirmation';
import CustomButton from '../../../CustomViews/Elements/CustomButton';
import CustomListHeader from '../../../CustomViews/Pages/ListComponent/CustomListHeader';
import CustomListSearch from '../../../CustomViews/Pages/ListComponent/Search/CustomListSearch';
import AddEditRoleModal from './AddEditRoleModal';
import NewTab from '../../../CustomViews/Pages/NewTab/NewTab';

export default function LicensesList() {
    const navigate = useNavigate();
    const location = useLocation()

    //search params
    const queryParams = new URLSearchParams(location.search);
    const [searchParams, setSearchParams] = useSearchParams(queryParams);

    //use states
    const [openDelete, setopenDelete] = useState(false);
    const [openAddEditModal, setopenAddEditModal] = useState(false);
    let [selectedRole, setselectedRole] = useState<Role>(new Role());
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        sortBy: QueryBuilderHelper.DefaultSortValue(RolesField.NAME),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });

    const q_queryKey = [CachingEnum.ACCESSIBILITIES, queryStr]
    const { data, fetchStatus, deleteRoleById } = useRolesHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: RolesField.NAME,
            title: "Role",
            sortable: true,
            render: (record: Role) => (record?.[RolesField.PUBLIC_ID] ? 
                <NewTab 
                linkTitle={record?.[RolesField.NAME] != null && record?.[RolesField.NAME] != "" ? record?.[RolesField.NAME] : "--"}
                linkUrl={`${RouteEnum.ACCESSIBILITY_DETAILS}?id=${record?.[RolesField.PUBLIC_ID] }`} /> : "--"
            ),
        },

        {
            accessor: RolesField.DESCRIPTION,
            title: "Description",
            sortable: true,
            render: (record: Role) => record?.[RolesField.DESCRIPTION] ?? "0",
        },
        {
            accessor: RolesField.CREATED_DATE,
            title: 'Created Date',
            sortable: true,
            render: (record: Role) => record?.[RolesField.CREATED_DATE] ? DateHelper.getFormattedDate(record?.[RolesField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: RolesField.UPDATED_DATE,
            title: 'Last Updated',
            sortable: true,
            render: (record: Role) => record?.[RolesField.UPDATED_DATE] ? DateHelper.getFormattedDate(record?.[RolesField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--",
        },
        {
            accessor: 'action',
            title: "Action",
            render: (record: Role) => <>
                <CustomIconButton toolTipName='View'
                    icon={<IconEye />}
                    onClick={(e: any) => { 
                        navigate(`${RouteEnum.ACCESSIBILITY_DETAILS}?id=${record?.[RolesField.PUBLIC_ID]}`, { replace: true })
                    }} />
                {AccessibilityHelper.hasAccess(AdminModules.ACCESSIBILITIES_EDIT) && <CustomIconButton toolTipName='Edit'
                    icon={<IconEdit />}
                    onClick={(e: any) => {
                        setopenAddEditModal(true);
                        setselectedRole(record);
                    }} />}
                {AccessibilityHelper.hasAccess(AdminModules.ACCESSIBILITIES_DELETE) && <CustomIconButton toolTipName='Delete'
                    icon={<IconTrash color='var(--buttonHoverBGDangerColor)' />}
                    onClick={(e: any) => { 
                        setopenDelete(true);
                        setselectedRole(record);
                    }} />}
            </>
        },
    ];

    useEffect(() => {
        setSearchParams(QueryBuilderHelper.CleanQueryDictionary(queryStr));
    }, [queryStr]);

    return <TableContainer>
        <CustomListHeader title={RouteHelper.getRouteDescription(RouteEnum.ACCESSIBILITIES)}
            subTitle=""
            headerButtons={<>
                {AccessibilityHelper.hasAccess(AdminModules.ACCESSIBILITIES_CREATE) && <CustomButton
                    btnType={ButtonType.PRIMARY}
                    title='Add New Role'
                    startIcon={<IconPlus />}
                    // isRoundedCorner={true}
                    onClick={(e: any) => {
                        setopenAddEditModal(true);
                        setselectedRole(new Role());
                    }} />}
            </>}
        />
        <CustomListSearch searchValue={queryStr.searchString ?? ""}
            onChangeSearch={(searchString) => setQueryStr(prev => ({ ...prev, searchString: searchString, pageNumber: 1 }))}
            searchPlaceholder='Search Role'
            hideFilterBtn/>
        <CustomListComponent
            columns={tableColumns}
            fetching={fetchStatus == "fetching"}
            idAccessor={(record: Role) => `accessibility${record?.[RolesField.PUBLIC_ID]}`}
            records={data?.data?.list ?? []}
            totalRecords={data?.data?.metaData?.totalCount ?? 0}
            page={queryStr.pageNumber}
            recordsPerPage={queryStr.pageSize}
            defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
            onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
            setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
            onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />

        {openDelete && <CustomDeleteConfirmation
            openDialog={openDelete}
            ItemToDelete={selectedRole?.[RolesField.NAME] ?? null} // Optional to display name of the item to be deleted
            handleClose={() => {
                setopenDelete(false);
                setselectedRole(new Role());
            }}
            showRemarksField={true}
            deleteItem={async (remarks: any) => {
                if(selectedRole?.[RolesField.PUBLIC_ID]) {
                    let deleteReponse: any = await deleteRoleById(selectedRole?.[RolesField.PUBLIC_ID], remarks);

                    if(deleteReponse?.isSuccess) {
                        setopenDelete(false);
                        setselectedRole(new Role());
                    }
                }
            }} />
        }

        {openAddEditModal && <AddEditRoleModal
            openAddEditModal={openAddEditModal}
            setopenAddEditModal={setopenAddEditModal}
            selectedRole={selectedRole}
            setselectedRole={setselectedRole} />}
    </TableContainer>
}
